import type { ISODate, NutritionTarget } from "../domain/model";
import { flexCalorieTargetForDate, loadFlexCaloriePlan } from "./flex-calorie-plan";

export type FormulaSex = "female" | "male";
export type GoalDirection = "lose" | "maintain" | "gain";
export type ActivityLevel = "sedentary" | "light" | "moderate" | "high";

export interface GoalAnswers {
  displayName?: string;
  sex: FormulaSex;
  age: number;
  heightCm: number;
  weightKg: number;
  direction: GoalDirection;
  targetWeightKg: number;
  days: number;
  activity: ActivityLevel;
  weekendFlex: boolean;
}

export interface GoalPlan {
  answers: GoalAnswers;
  ree: number;
  tdee: number;
  totalEnergyChange: number;
  requestedDailyAdjustment: number;
  appliedDailyAdjustment: number;
  dailyCalories: number;
  weekdayCalories: number;
  weekendCalories: number;
  proteinGrams: number;
  fatGrams: number;
  carbsGrams: number;
  paceKgPerWeek: number;
  recommendedDays: number;
  adjustedForSafety: boolean;
  warning: string | null;
  createdAt: string;
}

const STORAGE_KEY = "yumnote.goal-plan.v1";
const activityFactor: Record<ActivityLevel, number> = { sedentary: 1.2, light: 1.375, moderate: 1.55, high: 1.725 };
const roundTo = (value: number, step = 1) => Math.round(value / step) * step;

export function calculateGoalPlan(answers: GoalAnswers, now = new Date()): GoalPlan {
  const ree = 10 * answers.weightKg + 6.25 * answers.heightCm - 5 * answers.age + (answers.sex === "male" ? 5 : -161);
  const tdee = ree * activityFactor[answers.activity];
  const changeKg = answers.targetWeightKg - answers.weightKg;
  const totalEnergyChange = Math.abs(changeKg) * 7_700;
  const requested = answers.direction === "maintain" ? 0 : totalEnergyChange / Math.max(1, answers.days);
  const sign = answers.direction === "lose" ? -1 : answers.direction === "gain" ? 1 : 0;
  const maxAdjustment = answers.direction === "lose" ? Math.min(750, tdee - 1_000) : answers.direction === "gain" ? 500 : 0;
  const applied = Math.max(0, Math.min(requested, maxAdjustment));
  const dailyCalories = roundTo(Math.max(1_000, tdee + sign * applied), 10);
  const weekendBonus = answers.weekendFlex ? Math.min(500, roundTo(dailyCalories * 0.15, 10)) : 0;
  const weekendCalories = dailyCalories + weekendBonus;
  const weekdayCalories = answers.weekendFlex ? roundTo((dailyCalories * 7 - weekendCalories * 2) / 5) : dailyCalories;
  const referenceWeight = answers.direction === "lose" ? Math.max(answers.targetWeightKg, answers.weightKg * 0.85) : answers.weightKg;
  const proteinGrams = roundTo(referenceWeight * (answers.direction === "maintain" ? 1.4 : 1.6), 5);
  const fatGrams = roundTo(Math.max(referenceWeight * 0.8, dailyCalories * 0.25 / 9), 5);
  const carbsGrams = roundTo(Math.max(0, (dailyCalories - proteinGrams * 4 - fatGrams * 9) / 4), 5);
  const recommendedDays = requested > applied && applied > 0 ? Math.ceil(totalEnergyChange / applied) : answers.days;
  const targetBmi = answers.targetWeightKg / ((answers.heightCm / 100) ** 2);
  const invalidBmi = answers.direction === "lose" && targetBmi < 18.5;
  const adjustedForSafety = invalidBmi || requested > applied;
  const warning = invalidBmi
    ? "Целевой вес ниже BMI 18,5. Автоматический дефицит не назначен — такую цель лучше обсудить со специалистом."
    : requested > applied
      ? `Запрошенный темп требует слишком большой поправки. План смягчён; реалистичный срок — около ${recommendedDays} дней.`
      : null;
  return {
    answers, ree: roundTo(ree), tdee: roundTo(tdee), totalEnergyChange: roundTo(totalEnergyChange),
    requestedDailyAdjustment: roundTo(requested), appliedDailyAdjustment: invalidBmi ? 0 : roundTo(applied),
    dailyCalories: invalidBmi ? roundTo(tdee, 10) : dailyCalories,
    weekdayCalories: invalidBmi ? roundTo(tdee, 10) : weekdayCalories,
    weekendCalories: invalidBmi ? roundTo(tdee, 10) : weekendCalories,
    proteinGrams, fatGrams, carbsGrams, paceKgPerWeek: Math.round(Math.abs(changeKg) / Math.max(1, answers.days) * 70) / 10,
    recommendedDays, adjustedForSafety, warning, createdAt: now.toISOString(),
  };
}

export function saveGoalPlan(plan: GoalPlan): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plan));
  window.dispatchEvent(new Event("yumnote:goal-plan"));
}

export function loadGoalPlan(): GoalPlan | null {
  try { const raw = localStorage.getItem(STORAGE_KEY); return raw ? JSON.parse(raw) as GoalPlan : null; } catch { return null; }
}

export function targetForDate(date: ISODate): NutritionTarget | null {
  const plan = loadGoalPlan();
  const flexPlan = loadFlexCaloriePlan();
  if (!plan && !flexPlan) return null;
  const day = new Date(`${date}T12:00:00`).getDay();
  const calories = flexCalorieTargetForDate(date, flexPlan) ?? (day === 0 || day === 6 ? plan!.weekendCalories : plan!.weekdayCalories);
  const createdAt = plan?.createdAt ?? flexPlan!.updatedAt;
  return {
    id: "local-goal", effectiveFrom: createdAt.slice(0, 10), effectiveTo: null,
    dailyCalories: calories, proteinGrams: plan?.proteinGrams ?? 110, fatGrams: plan?.fatGrams ?? 75, carbsGrams: plan?.carbsGrams ?? 300,
    waterGlasses: 8, createdAt, updatedAt: createdAt, deletedAt: null, syncStatus: "pending",
  };
}
